import React, { useState } from 'react';
import { useAuth } from './AuthContext';
import { CREATOR_SIGNUPS_ENABLED } from './config';
import { functions, storage } from './firebaseConfig';
import { httpsCallable } from 'firebase/functions';
import { ref, uploadString } from 'firebase/storage';

function CreatorOnboarding({ onComplete }) {
  const { completeCreatorOnboarding } = useAuth();
  const [step, setStep] = useState(1);
  const [isTransitioning, setIsTransitioning] = useState(false);
  const [channelUrl, setChannelUrl] = useState('');
  const [promotionalUrl, setPromotionalUrl] = useState('');
  const [platform, setPlatform] = useState('');
  const [contentType, setContentType] = useState('');
  const [profileImage, setProfileImage] = useState(null);
  const [profileImagePreview, setProfileImagePreview] = useState('');
  const [subscriberCount, setSubscriberCount] = useState('');
  const [creatorGoal, setCreatorGoal] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [youtubeChannelData, setYoutubeChannelData] = useState(null);

  // Check if creator signups are disabled
  if (!CREATOR_SIGNUPS_ENABLED) {
    return (
      <div style={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        minHeight: '100vh',
        background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
        padding: '20px',
        textAlign: 'center'
      }}>
        <div style={{
          background: 'rgba(255, 255, 255, 0.1)',
          backdropFilter: 'blur(10px)',
          borderRadius: '20px',
          padding: '40px',
          maxWidth: '500px',
          color: 'white'
        }}>
          <h2 style={{ fontSize: '28px', marginBottom: '16px' }}>Creator Signups Closed</h2>
          <p style={{ fontSize: '16px', opacity: 0.9, marginBottom: '24px' }}>
            We're currently testing with a limited group of creators. Check back soon!
          </p>
          <button
            onClick={onComplete}
            style={{
              padding: '12px 32px',
              background: 'white',
              color: '#667eea',
              border: 'none',
              borderRadius: '8px',
              fontSize: '16px',
              fontWeight: 'bold',
              cursor: 'pointer'
            }}
          >
            Back to App
          </button>
        </div>
      </div>
    );
  }

  // Fetch YouTube channel data
  const fetchYouTubeChannelData = async (channelUrl) => {
    try {
      // Call Cloud Function to get YouTube data (keeps API key secure)
      const getYouTubeData = httpsCallable(functions, 'getYouTubeChannelData');
      const result = await getYouTubeData({ channelUrl });

      if (result.data.success) {
        return {
          name: result.data.displayName,
          photoURL: result.data.photoURL,
          channelId: result.data.channelId
        };
      }

      return null;
    } catch (error) {
      console.error('Error fetching YouTube channel data:', error);
      return null;
    }
  };

  // Fetch Twitch channel data
  const fetchTwitchChannelData = async (channelUrl) => {
    try {
      // Call Cloud Function to get Twitch data (keeps client secret secure)
      const getTwitchData = httpsCallable(functions, 'getTwitchChannelData');
      const result = await getTwitchData({ channelUrl });

      if (result.data.success) {
        return {
          name: result.data.displayName,
          photoURL: result.data.photoURL,
          channelId: channelUrl // Use URL as identifier
        };
      }

      return null;
    } catch (error) {
      console.error('Error fetching Twitch channel data:', error);
      return null;
    }
  };

  const handleStep1Submit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    if (!channelUrl.trim()) {
      setError('Please enter your channel URL');
      setLoading(false);
      return;
    }

    // Auto-add https:// if missing
    let validUrl = channelUrl.trim();
    if (!validUrl.startsWith('http://') && !validUrl.startsWith('https://')) {
      validUrl = 'https://' + validUrl;
    }

    // Update the channel URL with the corrected version
    setChannelUrl(validUrl);

    // Basic URL validation
    const urlPattern = /^https?:\/\/.+/i;
    if (!urlPattern.test(validUrl)) {
      setError('Please enter a valid URL');
      setLoading(false);
      return;
    }

    // Extract platform from URL
    let detectedPlatform = '';
    if (validUrl.includes('youtube.com') || validUrl.includes('youtu.be')) {
      detectedPlatform = 'YouTube';
      
      // Verify YouTube channel exists via Cloud Function
      try {
        const verifyUrl = httpsCallable(functions, 'verifyAndLockPlatformUrl');
        const result = await verifyUrl({ platform: 'YouTube', channelUrl: validUrl });
        
        if (result.data.success && result.data.verified) {
          setYoutubeChannelData({
            name: result.data.displayName,
            photoURL: result.data.photoURL,
            verified: true
          });
          setError(''); // Clear any previous errors
        }
      } catch (error) {
        console.error('YouTube verification error:', error);
        setError(error.message || 'Could not verify YouTube channel. Please check the URL.');
        setLoading(false);
        return;
      }
    } else if (validUrl.includes('twitch.tv')) {
      detectedPlatform = 'Twitch';
    } else if (validUrl.includes('instagram.com')) {
      detectedPlatform = 'Instagram';
    } else if (validUrl.includes('tiktok.com')) {
      detectedPlatform = 'TikTok';
    }
    
    setPlatform(detectedPlatform);
    setLoading(false);
    
    // Trigger slide animation
    setIsTransitioning(true);
    setTimeout(() => {
      setStep(2);
      setIsTransitioning(false);
    }, 300);
  };

  const handleStep2Submit = async (e) => {
    e.preventDefault();
    setError('');
    
    // Trigger slide animation to step 3
    setIsTransitioning(true);
    setTimeout(() => {
      setStep(3);
      setIsTransitioning(false);
    }, 300);
  };

  const handleProfileImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setProfileImage(file);
      const reader = new FileReader();
      reader.onloadend = () => {
        setProfileImagePreview(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleStep3Submit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      // Upload creator goal to Firebase Storage
      if (creatorGoal.trim()) {
        const goalRef = ref(storage, `creator-goals/${Date.now()}_${channelUrl.replace(/[^a-zA-Z0-9]/g, '_')}.txt`);
        await uploadString(goalRef, creatorGoal);
      }

      // Pass all data to the completion function
      await completeCreatorOnboarding(
        channelUrl, 
        promotionalUrl, 
        platform, 
        contentType,
        youtubeChannelData, // Pass the YouTube/Twitch data
        profileImage,
        subscriberCount,
        creatorGoal
      );
      onComplete();
    } catch (error) {
      console.error('Error completing onboarding:', error);
      setError(error.message || 'Failed to complete setup. Please try again.');
      setLoading(false);
    }
  };

  return (
    <div className="modal-overlay" style={{ zIndex: 1000 }}>
      <div className="modal-content" style={{ maxWidth: '500px', overflow: 'hidden' }}>
        {/* Step Indicators */}
        <div className="flex justify-center gap-2 mb-6">
          <div className={`w-2 h-2 rounded-full ${step === 1 ? 'bg-purple-600' : 'bg-gray-300'}`}></div>
          <div className={`w-2 h-2 rounded-full ${step === 2 ? 'bg-purple-600' : 'bg-gray-300'}`}></div>
          <div className={`w-2 h-2 rounded-full ${step === 3 ? 'bg-purple-600' : 'bg-gray-300'}`}></div>
        </div>

        <div 
          className="transition-all duration-300 ease-in-out"
          style={{
            transform: isTransitioning ? 'translateX(100%)' : 'translateX(0)',
            opacity: isTransitioning ? 0 : 1
          }}
        >
        {step === 1 ? (
          <>
            <h2 className="text-2xl font-bold mb-2 text-center">Welcome, Creator! 🎉</h2>
            <p className="text-black mb-6 text-center">Let's set up your profile</p>

            {error && (
              <div className="bg-red-500/20 border border-red-500 text-red-200 px-4 py-3 rounded mb-4">
                {error}
              </div>
            )}

            <form onSubmit={handleStep1Submit}>
              <div className="mb-6">
                <label className="block text-sm font-medium mb-2">
                  Channel URL *
                </label>
                <input
                  type="text"
                  value={channelUrl}
                  onChange={(e) => setChannelUrl(e.target.value)}
                  placeholder="https://youtube.com/@yourchannel"
                  className="w-full px-4 py-3 bg-gray-800 border border-gray-700 rounded-lg focus:outline-none focus:border-purple-500 text-white placeholder-gray-400"
                />
                <p className="text-xs text-gray-400 mt-2">
                  Your YouTube, Twitch, Instagram, or TikTok channel URL
                </p>
              </div>

              <button
                type="submit"
                disabled={loading}
                className="w-full bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700 text-white font-bold py-3 px-6 rounded-lg transition"
              >
                {loading ? 'Checking...' : 'Next'}
              </button>
            </form>
          </>
        ) : step === 2 ? (
          <>
            <h2 className="text-2xl font-bold mb-2 text-center">Almost Done! 🚀</h2>
            <p className="text-black mb-6 text-center">Add your promotional link</p>

            {error && (
              <div className="bg-red-500/20 border border-red-500 text-red-200 px-4 py-3 rounded mb-4">
                {error}
              </div>
            )}

            <form onSubmit={handleStep2Submit}>
              <div className="mb-4">
                <label className="block text-sm font-medium mb-2">
                  Promotional URL (Optional)
                </label>
                <input
                  type="text"
                  value={promotionalUrl}
                  onChange={(e) => setPromotionalUrl(e.target.value)}
                  placeholder="https://linktr.ee/yourprofile"
                  className="w-full px-4 py-3 bg-gray-800 border border-gray-700 rounded-lg focus:outline-none focus:border-purple-500 text-white placeholder-gray-400"
                />
                <p className="text-xs text-gray-400 mt-2">
                  Linktree, Beacons, or other promotional link
                </p>
              </div>

              <div className="mb-4">
                <label className="block text-sm font-medium mb-2">
                  Platform
                </label>
                <select
                  value={platform}
                  onChange={(e) => setPlatform(e.target.value)}
                  className="w-full px-4 py-3 bg-gray-800 border border-gray-700 rounded-lg focus:outline-none focus:border-purple-500 text-white"
                >
                  <option value="">Select platform</option>
                  <option value="YouTube">YouTube</option>
                  <option value="Twitch">Twitch</option>
                  <option value="Instagram">Instagram</option>
                  <option value="TikTok">TikTok</option>
                </select>
              </div>

              <div className="mb-6">
                <label className="block text-sm font-medium mb-2">
                  Content Type
                </label>
                <select
                  value={contentType}
                  onChange={(e) => setContentType(e.target.value)}
                  className="w-full px-4 py-3 bg-gray-800 border border-gray-700 rounded-lg focus:outline-none focus:border-purple-500 text-white"
                >
                  <option value="">Select type</option>
                  <option value="Gaming">Gaming</option>
                  <option value="Vlog">Vlog</option>
                  <option value="Tutorial">Tutorial</option>
                  <option value="Comedy">Comedy</option>
                  <option value="Reactions">Reactions</option>
                  <option value="Reviews">Reviews</option>
                  <option value="Health">Health & Wellness</option>
                  <option value="Beauty">Beauty & Fashion</option>
                  <option value="Music">Music</option>
                  <option value="Art">Art & Design</option>
                  <option value="Education">Education</option>
                  <option value="Talk">Talk/Podcast</option>
                  <option value="IRL">IRL</option>
                  <option value="Other">Other</option>
                </select>
              </div>

              <div className="flex gap-3">
                <button
                  type="button"
                  onClick={() => {
                    setIsTransitioning(true);
                    setTimeout(() => {
                      setStep(1);
                      setIsTransitioning(false);
                    }, 300);
                  }}
                  disabled={loading}
                  className="flex-1 bg-gray-700 hover:bg-gray-600 text-white font-bold py-3 px-6 rounded-lg transition disabled:opacity-50"
                >
                  Back
                </button>
                <button
                  type="submit"
                  className="flex-1 bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700 text-white font-bold py-3 px-6 rounded-lg transition"
                >
                  Next
                </button>
              </div>
            </form>
          </>
        ) : step === 3 ? (
          <>
            <h2 className="text-2xl font-bold mb-2 text-center">Final Details! ✨</h2>
            <p className="text-black mb-6 text-center">Help us showcase your channel</p>

            {error && (
              <div className="bg-red-500/20 border border-red-500 text-red-200 px-4 py-3 rounded mb-4">
                {error}
              </div>
            )}

            <form onSubmit={handleStep3Submit}>
              <div className="mb-4">
                <label className="block text-sm font-medium mb-2">
                  Profile Picture *
                </label>
                <div className="flex items-center gap-4">
                  {profileImagePreview && (
                    <img 
                      src={profileImagePreview} 
                      alt="Preview" 
                      className="w-16 h-16 rounded-full object-cover border-2 border-purple-500"
                    />
                  )}
                  <input
                    type="file"
                    accept="image/*"
                    onChange={handleProfileImageChange}
                    className="flex-1 px-4 py-3 bg-gray-800 border border-gray-700 rounded-lg focus:outline-none focus:border-purple-500 text-sm text-white"
                  />
                </div>
                <p className="text-xs text-gray-400 mt-2">
                  Upload a clear profile picture
                </p>
              </div>

              <div className="mb-4">
                <label className="block text-sm font-medium mb-2">
                  Subscriber/Follower Count *
                </label>
                <input
                  type="number"
                  value={subscriberCount}
                  onChange={(e) => setSubscriberCount(e.target.value)}
                  placeholder="1000"
                  className="w-full px-4 py-3 bg-gray-800 border border-gray-700 rounded-lg focus:outline-none focus:border-purple-500 text-white placeholder-gray-400"
                />
                <p className="text-xs text-gray-400 mt-2">
                  Your current subscriber or follower count
                </p>
              </div>

              <div className="mb-6">
                <label className="block text-sm font-medium mb-2">
                  Your Goal as a Creator
                </label>
                <textarea
                  value={creatorGoal}
                  onChange={(e) => setCreatorGoal(e.target.value)}
                  placeholder="Share your goals and aspirations..."
                  rows="3"
                  maxLength="100"
                  className="w-full px-4 py-3 bg-gray-800 border border-gray-700 rounded-lg focus:outline-none focus:border-purple-500 resize-none text-white placeholder-gray-400"
                />
                <p className="text-xs text-gray-400 mt-2">
                  {creatorGoal.length}/100 characters
                </p>
              </div>

              <div className="flex gap-3">
                <button
                  type="button"
                  onClick={() => {
                    setIsTransitioning(true);
                    setTimeout(() => {
                      setStep(2);
                      setIsTransitioning(false);
                    }, 300);
                  }}
                  disabled={loading}
                  className="flex-1 bg-gray-700 hover:bg-gray-600 text-white font-bold py-3 px-6 rounded-lg transition disabled:opacity-50"
                >
                  Back
                </button>
                <button
                  type="submit"
                  disabled={loading}
                  className="flex-1 bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700 text-white font-bold py-3 px-6 rounded-lg transition disabled:opacity-50"
                >
                  {loading ? 'Completing...' : 'Complete Setup'}
                </button>
              </div>
            </form>
          </>
        ) : (
          <>
            <h2 className="text-2xl font-bold mb-2 text-center">Almost Done! 🚀</h2>
            <p className="text-black mb-6 text-center">Add your promotional link</p>

            {error && (
              <div className="bg-red-500/20 border border-red-500 text-red-200 px-4 py-3 rounded mb-4">
                {error}
              </div>
            )}

            <form onSubmit={handleStep2Submit}>
              <div className="mb-4">
                <label className="block text-sm font-medium mb-2">
                  Promotional URL (Optional)
                </label>
                <input
                  type="text"
                  value={promotionalUrl}
                  onChange={(e) => setPromotionalUrl(e.target.value)}
                  placeholder="https://linktr.ee/yourprofile"
                  className="w-full px-4 py-3 bg-gray-800 border border-gray-700 rounded-lg focus:outline-none focus:border-purple-500"
                />
                <p className="text-xs text-gray-400 mt-2">
                  Linktree, Beacons, or other promotional link
                </p>
              </div>

              <div className="mb-4">
                <label className="block text-sm font-medium mb-2">
                  Platform
                </label>
                <select
                  value={platform}
                  onChange={(e) => setPlatform(e.target.value)}
                  className="w-full px-4 py-3 bg-gray-800 border border-gray-700 rounded-lg focus:outline-none focus:border-purple-500"
                >
                  <option value="">Select platform</option>
                  <option value="YouTube">YouTube</option>
                  <option value="Twitch">Twitch</option>
                  <option value="Instagram">Instagram</option>
                  <option value="TikTok">TikTok</option>
                  <option value="Other">Other</option>
                </select>
              </div>

              <div className="mb-6">
                <label className="block text-sm font-medium mb-2">
                  Content Type
                </label>
                <select
                  value={contentType}
                  onChange={(e) => setContentType(e.target.value)}
                  className="w-full px-4 py-3 bg-gray-800 border border-gray-700 rounded-lg focus:outline-none focus:border-purple-500"
                >
                  <option value="">Select type</option>
                  <option value="Gaming">Gaming</option>
                  <option value="Vlog">Vlog</option>
                  <option value="Tutorial">Tutorial</option>
                  <option value="Comedy">Comedy</option>
                  <option value="Reactions">Reactions</option>
                  <option value="Reviews">Reviews</option>
                  <option value="Health">Health & Wellness</option>
                  <option value="Beauty">Beauty & Fashion</option>
                  <option value="Music">Music</option>
                  <option value="Art">Art & Design</option>
                  <option value="Education">Education</option>
                  <option value="Talk">Talk/Podcast</option>
                  <option value="IRL">IRL</option>
                  <option value="Other">Other</option>
                </select>
              </div>

              <div className="flex gap-3">
                <button
                  type="button"
                  onClick={() => {
                    setIsTransitioning(true);
                    setTimeout(() => {
                      setStep(1);
                      setIsTransitioning(false);
                    }, 300);
                  }}
                  className="flex-1 bg-gray-700 hover:bg-gray-600 text-white font-bold py-3 px-6 rounded-lg transition"
                >
                  Back
                </button>
                <button
                  type="submit"
                  className="flex-1 bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700 text-white font-bold py-3 px-6 rounded-lg transition"
                >
                  Next
                </button>
              </div>
            </form>
          </>
        )}
        </div>
      </div>
    </div>
  );
}

export default CreatorOnboarding;
